import { useState } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { Icon } from "../components/ui/Icon";
import { submitPreRegisto, submitPedidoInfo } from "../lib/db";
import logoEscuro from "../assets/logo_escuro.svg";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Paleta dark — espelha as variáveis --vg-* do modo escuro da app ──────────
const C = {
  bg:          "#0f172a",   // --vg-bg dark
  bgCard:      "#1e293b",   // --vg-card dark
  bgAlt:       "#131d2e",   // variação subtil entre secções
  bgHover:     "#243347",   // --vg-card-hover dark
  primary:     "#3b82f6",   // --vg-primary dark
  primaryHov:  "#2563eb",   // --vg-primary-hover dark
  primaryDark: "#1d4ed8",
  primaryGlow: "rgba(59,130,246,0.18)",
  primaryBdr:  "#1e40af",   // --vg-primary-border dark
  text:        "#f1f5f9",   // --vg-text dark
  textMid:     "#cbd5e1",   // --vg-text-mid dark
  textMuted:   "#94a3b8",   // --vg-text-muted dark
  textLight:   "#8896ab",   // --vg-text-light dark (contrast-safe)
  border:      "#334155",   // --vg-border dark
  borderLight: "#243040",   // --vg-border-light dark
  green:       "#4ade80",   // --vg-success dark
  white:       "#ffffff",
};

// ─── Mini mockup da app — versão dark ─────────────────────────────────────────
const AppMockup = () => (
  <div style={{
    width: 440, height: 270, borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 0 0 1px rgba(59,130,246,0.15), 0 32px 80px rgba(0,0,0,0.7), 0 0 100px rgba(59,130,246,0.08)",
    transform: "perspective(1000px) rotateY(-8deg) rotateX(3deg)",
    display: "flex", flexShrink: 0,
  }}>
    {/* Sidebar */}
    <div style={{ width: 54, background: "#080f1d", display: "flex", flexDirection: "column", padding: "12px 8px", gap: 5, flexShrink: 0, borderRight: `1px solid ${C.borderLight}` }}>
      <div style={{ display: "flex", gap: 2, marginBottom: 10, paddingLeft: 2 }}>
        <div style={{ width: 4, height: 14, borderRadius: 2, background: "#3b82f6" }} />
        <div style={{ width: 4, height: 10, borderRadius: 2, background: "#60a5fa", alignSelf: "flex-end" }} />
        <div style={{ width: 4, height: 7,  borderRadius: 2, background: "#93c5fd", alignSelf: "flex-end" }} />
      </div>
      {[true, false, false, false, false].map((active, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 4px", borderRadius: 5, background: active ? "rgba(59,130,246,0.2)" : "transparent" }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: active ? C.primary : C.border }} />
          <div style={{ width: 22, height: 4, borderRadius: 2, background: active ? C.primary : "#2d3e52" }} />
        </div>
      ))}
      <div style={{ marginTop: "auto", width: 22, height: 22, borderRadius: "50%", background: C.primary, alignSelf: "center" }} />
    </div>

    {/* Main content */}
    <div style={{ flex: 1, padding: 12, background: C.bg, display: "flex", flexDirection: "column", gap: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ width: 70, height: 8, borderRadius: 4, background: C.textMid }} />
        <div style={{ width: 54, height: 16, borderRadius: 6, background: C.primary }} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
        {["rgba(59,130,246,0.12)", "rgba(139,92,246,0.12)", "rgba(52,211,153,0.12)"].map((bg, i) => (
          <div key={i} style={{ background: C.bgCard, borderRadius: 7, padding: "7px 8px", border: `1px solid ${C.border}` }}>
            <div style={{ width: 20, height: 9, borderRadius: 3, background: C.textMid, marginBottom: 3 }} />
            <div style={{ width: 36, height: 4, borderRadius: 2, background: C.textLight }} />
            <div style={{ width: 18, height: 18, borderRadius: 5, background: bg, marginTop: 5, border: `1px solid ${C.border}` }} />
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, flex: 1 }}>
        {[0, 1].map(col => (
          <div key={col} style={{ background: C.bgCard, borderRadius: 7, padding: 8, border: `1px solid ${C.border}` }}>
            <div style={{ width: 44, height: 4, borderRadius: 2, background: C.textLight, marginBottom: 7 }} />
            {[["#1e40af","rgba(59,130,246,0.15)"],["#166534","rgba(52,211,153,0.15)"],["#854d0e","rgba(251,191,36,0.15)"]].map(([bd, bg], row) => (
              <div key={row} style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 5 }}>
                <div style={{ flex: 1, height: 4, borderRadius: 2, background: C.border }} />
                <div style={{ width: 26, height: 11, borderRadius: 10, background: bg, border: `1px solid ${bd}` }} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Feature cards ─────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: "tickets",   title: "Gestão de Ocorrências",    desc: "Registe anomalias e pedidos dos condóminos. Kanban visual por estado com histórico e responsáveis." },
  { icon: "docs",      title: "Documentos Centralizados", desc: "Atas, regulamentos e contratos num arquivo digital. Acesso imediato, sem papéis perdidos." },
  { icon: "suppliers", title: "Directório de Fornecedores", desc: "Registo completo de prestadores de serviço com contactos, especialidade e observações." },
  { icon: "chat",      title: "Assistente com IA",        desc: "Consulte ocorrências, contactos e documentos do condomínio em linguagem natural." },
  { icon: "users",     title: "Gestão de Utilizadores",   desc: "Convide condóminos e administradores com permissões distintas por papel." },
  { icon: "home",      title: "Dashboard em Tempo Real",  desc: "Visão geral de ocorrências abertas, documentos recentes e actividade do condomínio." },
];

const STEPS = [
  { n: "01", title: "Registe o condomínio",    desc: "Configure os dados do condomínio, blocos e fracções em minutos." },
  { n: "02", title: "Convide os utilizadores", desc: "Adicione condóminos e administradores com as permissões adequadas." },
  { n: "03", title: "Opere a plataforma",      desc: "Gira ocorrências, documentos e fornecedores de qualquer dispositivo." },
];

// ── FeatureCard — extracted outside LandingPage so it keeps its own hover state ──
const FeatureCard = ({ icon, title, desc }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.bgHover : C.bgCard,
        borderRadius: 14, padding: 28,
        border: `1px solid ${hov ? "rgba(59,130,246,0.4)" : C.border}`,
        boxShadow: hov ? "0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(59,130,246,0.1)" : "0 1px 4px rgba(0,0,0,0.2)",
        transition: "all 0.22s",
      }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: "rgba(59,130,246,0.12)", border: `1px solid rgba(59,130,246,0.2)`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
        <Icon name={icon} size={22} color={C.primary} />
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 10 }}>{title}</h3>
      <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.7 }}>{desc}</p>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
export const LandingPage = ({ onEnterApp, onOpenPrivacy }) => {
  const isMobile = useIsMobile();
  const [wEmail, setWEmail]         = useState("");
  const [wSubmitted, setWSubmitted] = useState(false);
  const [wErr, setWErr]             = useState("");

  // Contact form state
  const [cForm, setCForm]           = useState({ nome: "", email: "", telefone: "", mensagem: "" });
  const [cSubmitted, setCSubmitted] = useState(false);
  const [cErr, setCErr]             = useState("");
  const [cLoading, setCLoading]     = useState(false);
  const [showContact, setShowContact] = useState(false);

  const openContact = () => {
    setCForm({ nome: "", email: "", telefone: "", mensagem: "" });
    setCSubmitted(false);
    setCErr("");
    setCLoading(false);
    setShowContact(true);
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const submitWaitlist = async () => {
    if (!wEmail.trim())                { setWErr("Introduza o seu email."); return; }
    if (!EMAIL_RE.test(wEmail.trim())) { setWErr("Introduza um email válido."); return; }
    setWErr("");
    try {
      await submitPreRegisto(wEmail.trim());
      setWSubmitted(true);
    } catch (err) {
      setWErr(err.message || "Erro ao registar. Tente novamente.");
    }
  };

  const submitContact = async () => {
    if (!cForm.nome.trim())    { setCErr("Introduza o seu nome."); return; }
    if (!cForm.email.trim())   { setCErr("Introduza o seu email."); return; }
    if (!EMAIL_RE.test(cForm.email.trim())) { setCErr("Introduza um email válido."); return; }
    if (!cForm.mensagem.trim()) { setCErr("Escreva a sua mensagem."); return; }
    setCErr("");
    setCLoading(true);
    try {
      await submitPedidoInfo(cForm);
      setCSubmitted(true);
    } catch (err) {
      setCErr(err.message || "Erro ao enviar. Tente novamente.");
    } finally {
      setCLoading(false);
    }
  };

  const cFld = (key) => (e) => { setCForm(f => ({ ...f, [key]: e.target.value })); setCErr(""); };

  const inputStyle = {
    width: "100%", padding: "12px 16px", fontSize: 14, borderRadius: 10,
    border: `1.5px solid ${C.border}`, background: C.bgCard, color: C.text,
    outline: "none", fontFamily: "inherit", transition: "border-color 0.15s",
  };

  // ── Navbar (JSX variable — not a component) ──────────────────────────────────
  const navbar = (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
      background: "rgba(15,23,42,0.88)", backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      borderBottom: `1px solid ${C.border}`,
      height: 64, display: "flex", alignItems: "center",
      justifyContent: "space-between", padding: "0 28px",
    }}>
      <img
        src={logoEscuro} alt="VillaGest"
        style={{ height: 28, width: "auto", cursor: "pointer" }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      />

      {!isMobile && (
        <div style={{ display: "flex", alignItems: "center", gap: 36 }}>
          {[["Funcionalidades","features"],["Como funciona","how"],["Lista de espera","waitlist"]].map(([label, id]) => (
            <button key={id} onClick={() => scrollTo(id)}
              style={{ background: "none", border: "none", cursor: "pointer", color: C.textMuted, fontSize: 14, fontWeight: 500, fontFamily: "inherit", transition: "color 0.15s", padding: 0 }}
              onMouseEnter={e => e.currentTarget.style.color = C.text}
              onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
              {label}
            </button>
          ))}
          <button onClick={openContact}
            style={{ background: "none", border: "none", cursor: "pointer", color: C.textMuted, fontSize: 14, fontWeight: 500, fontFamily: "inherit", transition: "color 0.15s", padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = C.text}
            onMouseLeave={e => e.currentTarget.style.color = C.textMuted}>
            Contacto
          </button>
          <button onClick={onEnterApp}
            style={{ background: C.primary, color: C.white, border: "none", borderRadius: 8, padding: "9px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background = C.primaryHov}
            onMouseLeave={e => e.currentTarget.style.background = C.primary}>
            Entrar na plataforma
          </button>
        </div>
      )}

      {isMobile && (
        <button onClick={onEnterApp}
          style={{ background: C.primary, color: C.white, border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Entrar
        </button>
      )}
    </nav>
  );

  // ── Hero ────────────────────────────────────────────────────────────────────
  const hero = (
    <section style={{
      minHeight: "100vh", paddingTop: 64,
      background: `radial-gradient(ellipse 85% 55% at 50% -5%, rgba(59,130,246,0.28) 0%, transparent 65%), ${C.bg}`,
      backgroundImage: `
        radial-gradient(ellipse 85% 55% at 50% -5%, rgba(59,130,246,0.28) 0%, transparent 65%),
        radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1px)
      `,
      backgroundSize: "auto, 28px 28px",
      display: "flex", alignItems: "center",
    }}>
      <div style={{
        maxWidth: 1120, margin: "0 auto",
        padding: isMobile ? "56px 24px 64px" : "80px 48px",
        display: "flex", alignItems: "center",
        gap: isMobile ? 0 : 72, flexDirection: isMobile ? "column" : "row",
      }}>
        {/* Texto */}
        <div style={{ flex: 1, minWidth: 0, animation: "fadeIn 0.7s ease", textAlign: isMobile ? "center" : "left" }}>
          {/* Badge */}
          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(59,130,246,0.12)", border: `1px solid rgba(59,130,246,0.3)`, borderRadius: 20, padding: "5px 14px", marginBottom: 28 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, boxShadow: "0 0 8px rgba(74,222,128,0.6)" }} />
            <span style={{ color: C.textMid, fontSize: 13, fontWeight: 500 }}>Gestão de Condomínio · Beta em breve</span>
          </div>

          <h1 style={{
            fontSize: isMobile ? 38 : 58, fontWeight: 800, lineHeight: 1.06,
            letterSpacing: "-2px", marginBottom: 22, color: C.text,
          }}>
            A plataforma que o<br />
            <span style={{ color: C.primary }}>seu condomínio</span> precisa
          </h1>

          <p style={{ fontSize: isMobile ? 16 : 19, color: C.textMuted, lineHeight: 1.7, marginBottom: 40, maxWidth: 500 }}>
            Ocorrências, documentos, fornecedores e assistente IA — tudo numa só plataforma. Para administradores e condóminos.
          </p>

          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: isMobile ? "center" : "flex-start" }}>
            <button
              onClick={() => scrollTo("waitlist")}
              style={{ background: C.primary, color: C.white, border: "none", borderRadius: 10, padding: "15px 30px", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: `0 0 0 1px ${C.primaryBdr}, 0 8px 24px rgba(59,130,246,0.35)`, transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = C.primaryHov; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 0 0 1px ${C.primaryDark}, 0 12px 32px rgba(59,130,246,0.45)`; }}
              onMouseLeave={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 0 0 1px ${C.primaryBdr}, 0 8px 24px rgba(59,130,246,0.35)`; }}>
              Entrar na lista de espera
            </button>
            <button
              onClick={() => scrollTo("features")}
              style={{ background: "transparent", color: C.textMid, border: `1px solid ${C.border}`, borderRadius: 10, padding: "15px 30px", fontSize: 16, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.textMuted; e.currentTarget.style.color = C.text; e.currentTarget.style.background = C.bgCard; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMid; e.currentTarget.style.background = "transparent"; }}>
              Ver funcionalidades
            </button>
          </div>

          <p style={{ marginTop: 22, fontSize: 13, color: C.textLight }}>
            Sem custos de configuração · Acesso por convite
          </p>
        </div>

        {/* Mockup */}
        {!isMobile && (
          <div style={{ flexShrink: 0, animation: "slideUp 0.9s ease 0.15s both" }}>
            <AppMockup />
          </div>
        )}
      </div>
    </section>
  );

  // ── Features ─────────────────────────────────────────────────────────────────
  const features = (
    <section id="features" style={{ background: C.bg, padding: isMobile ? "72px 24px" : "104px 48px", borderTop: `1px solid ${C.borderLight}` }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.primary, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 12 }}>
            Funcionalidades
          </span>
          <h2 style={{ fontSize: isMobile ? 30 : 42, fontWeight: 800, color: C.text, letterSpacing: "-0.5px", lineHeight: 1.1, marginBottom: 16 }}>
            Tudo o que precisa,<br />numa só plataforma
          </h2>
          <p style={{ fontSize: 17, color: C.textMuted, lineHeight: 1.65, maxWidth: 520, margin: "0 auto" }}>
            Pensada para simplificar a gestão diária de condomínios de qualquer dimensão.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 18 }}>
          {FEATURES.map((f, i) => <FeatureCard key={i} {...f} />)}
        </div>
      </div>
    </section>
  );

  // ── How it works ─────────────────────────────────────────────────────────────
  const howItWorks = (
    <section id="how" style={{ background: C.bgAlt, padding: isMobile ? "72px 24px" : "104px 48px", borderTop: `1px solid ${C.borderLight}`, borderBottom: `1px solid ${C.borderLight}` }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.primary, textTransform: "uppercase", letterSpacing: 1, display: "block", marginBottom: 12 }}>
            Como funciona
          </span>
          <h2 style={{ fontSize: isMobile ? 30 : 42, fontWeight: 800, color: C.text, letterSpacing: "-0.5px", marginBottom: 16 }}>
            Em três passos simples
          </h2>
          <p style={{ fontSize: 17, color: C.textMuted, lineHeight: 1.65 }}>
            Configure em minutos e comece a operar de imediato.
          </p>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 40 : 0 }}>
          {STEPS.map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "center", textAlign: isMobile ? "left" : "center", padding: isMobile ? 0 : "0 24px" }}>
                <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1, marginBottom: 16, background: `linear-gradient(135deg, ${C.primary}, rgba(59,130,246,0.3))`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  {s.n}
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 10 }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.7, maxWidth: 260 }}>{s.desc}</p>
              </div>
              {!isMobile && i < STEPS.length - 1 && (
                <div style={{ flexShrink: 0, width: 40, marginTop: 28, borderTop: `2px dashed ${C.border}` }} />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  // ── Testimonial ───────────────────────────────────────────────────────────────
  const testimonial = (
    <section style={{ background: C.bg, padding: isMobile ? "72px 24px" : "96px 48px" }}>
      <div style={{ maxWidth: 780, margin: "0 auto", textAlign: "center" }}>
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 20, padding: isMobile ? "40px 28px" : "56px 64px", position: "relative", overflow: "hidden" }}>
          {/* Glow */}
          <div style={{ position: "absolute", top: -40, left: "50%", transform: "translateX(-50%)", width: 200, height: 200, borderRadius: "50%", background: "rgba(59,130,246,0.06)", filter: "blur(40px)", pointerEvents: "none" }} />
          <div style={{ fontSize: 64, fontWeight: 800, color: C.primary, lineHeight: 0.8, fontFamily: "Georgia,serif", marginBottom: 24, opacity: 0.6 }}>"</div>
          <p style={{ fontSize: isMobile ? 18 : 22, fontWeight: 600, color: C.textMid, fontStyle: "italic", lineHeight: 1.6, marginBottom: 24 }}>
            Uma solução simples e directa para o que os condomínios realmente precisam. Tudo no mesmo lugar, sem complicações.
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${C.primary}, #8b5cf6)` }} />
            <div style={{ textAlign: "left" }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: C.text }}>Gonçalo Oliveira</p>
              <p style={{ fontSize: 12, color: C.textLight }}>Fundador, Portugal</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // ── Contact modal ───────────────────────────────────────────────────────────
  const contactModal = showContact && (
    <div
      onClick={() => setShowContact(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "fadeIn 0.2s ease",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 18,
          width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto",
          padding: isMobile ? "28px 22px" : "36px 32px",
          boxShadow: "0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(59,130,246,0.1)",
          animation: "slideUp 0.25s ease",
        }}
      >
        {/* Close button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: C.text, letterSpacing: "-0.3px" }}>
            Pedir mais informações
          </h2>
          <button
            onClick={() => setShowContact(false)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.textMuted} strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <p style={{ fontSize: 14, color: C.textMuted, lineHeight: 1.65, marginBottom: 24 }}>
          Tem dúvidas ou quer saber mais? Envie-nos uma mensagem e responderemos brevemente.
        </p>

        {cSubmitted ? (
          <div style={{ textAlign: "center", animation: "slideUp 0.3s ease", padding: "20px 0" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Icon name="check" size={24} color={C.green} />
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 8 }}>Mensagem enviada!</h3>
            <p style={{ fontSize: 15, color: C.textMuted, lineHeight: 1.65 }}>
              Obrigado pelo seu interesse. Entraremos em contacto brevemente.
            </p>
            <button
              onClick={() => setShowContact(false)}
              style={{ marginTop: 20, background: C.primary, color: C.white, border: "none", borderRadius: 10, padding: "11px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Fechar
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 16, flexDirection: isMobile ? "column" : "row" }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: C.textMid, display: "block", marginBottom: 6 }}>Nome *</label>
                <input
                  type="text" value={cForm.nome} onChange={cFld("nome")}
                  placeholder="O seu nome"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = C.primary}
                  onBlur={e => e.target.style.borderColor = C.border}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: C.textMid, display: "block", marginBottom: 6 }}>Email *</label>
                <input
                  type="email" value={cForm.email} onChange={cFld("email")}
                  placeholder="email@exemplo.pt"
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = C.primary}
                  onBlur={e => e.target.style.borderColor = C.border}
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.textMid, display: "block", marginBottom: 6 }}>Telefone <span style={{ fontWeight: 400, color: C.textLight }}>(opcional)</span></label>
              <input
                type="tel" value={cForm.telefone} onChange={cFld("telefone")}
                placeholder="+351 912 345 678"
                style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.primary}
                onBlur={e => e.target.style.borderColor = C.border}
              />
            </div>

            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: C.textMid, display: "block", marginBottom: 6 }}>Mensagem *</label>
              <textarea
                value={cForm.mensagem} onChange={cFld("mensagem")}
                placeholder="Escreva a sua mensagem ou dúvida..."
                rows={4}
                style={{ ...inputStyle, resize: "vertical", minHeight: 100 }}
                onFocus={e => e.target.style.borderColor = C.primary}
                onBlur={e => e.target.style.borderColor = C.border}
              />
            </div>

            {cErr && <p style={{ fontSize: 13, color: "#fca5a5" }}>{cErr}</p>}

            <button
              onClick={submitContact}
              disabled={cLoading}
              style={{
                background: C.primary, color: C.white, border: "none", borderRadius: 10,
                padding: "13px 28px", fontSize: 15, fontWeight: 700, cursor: cLoading ? "not-allowed" : "pointer",
                fontFamily: "inherit", transition: "all 0.2s", opacity: cLoading ? 0.7 : 1,
                boxShadow: `0 0 0 1px ${C.primaryBdr}, 0 4px 16px rgba(59,130,246,0.25)`,
                alignSelf: isMobile ? "stretch" : "flex-start",
              }}
              onMouseEnter={e => { if (!cLoading) { e.currentTarget.style.background = C.primaryHov; e.currentTarget.style.transform = "translateY(-1px)"; } }}
              onMouseLeave={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.transform = "none"; }}
            >
              {cLoading ? "A enviar..." : "Enviar mensagem"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ── Waitlist — gradiente azul profundo ───────────────────────────────────────
  const waitlist = (
    <section id="waitlist" style={{
      background: `linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 50%, #2563eb 100%)`,
      padding: isMobile ? "80px 24px" : "104px 48px",
      position: "relative", overflow: "hidden",
    }}>
      {/* Decorative glow orbs */}
      <div style={{ position: "absolute", top: -80, right: -80, width: 300, height: 300, borderRadius: "50%", background: "rgba(96,165,250,0.15)", filter: "blur(60px)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -60, left: -60, width: 250, height: 250, borderRadius: "50%", background: "rgba(30,64,175,0.3)", filter: "blur(50px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 620, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.85)", textTransform: "uppercase", letterSpacing: 1.2, display: "block", marginBottom: 16 }}>
          Acesso antecipado
        </span>
        <h2 style={{ fontSize: isMobile ? 28 : 38, fontWeight: 800, color: C.white, letterSpacing: "-0.5px", lineHeight: 1.15, marginBottom: 14 }}>
          Entre na lista de espera
        </h2>
        <p style={{ fontSize: 16, color: "rgba(255,255,255,0.88)", lineHeight: 1.7, marginBottom: 36 }}>
          Seja dos primeiros a ter acesso. Notificação assim que o beta abrir. Sem compromisso.
        </p>

        {wSubmitted ? (
          <div style={{ animation: "slideUp 0.3s ease" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Icon name="check" size={24} color={C.white} />
            </div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: C.white, marginBottom: 8 }}>Obrigado!</h3>
            <p style={{ fontSize: 15, color: "rgba(255,255,255,0.88)", lineHeight: 1.65 }}>
              O seu email foi registado. Entraremos em contacto assim que o acesso estiver disponível.
            </p>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 10, flexDirection: isMobile ? "column" : "row" }}>
              <input
                type="email"
                value={wEmail}
                onChange={e => { setWEmail(e.target.value); setWErr(""); }}
                onKeyDown={e => e.key === "Enter" && submitWaitlist()}
                placeholder="O seu email..."
                style={{
                  flex: 1, padding: "14px 18px", fontSize: 15, borderRadius: 10,
                  border: `1.5px solid ${wErr ? "#fca5a5" : "rgba(255,255,255,0.25)"}`,
                  background: "rgba(255,255,255,0.1)", color: C.white, outline: "none",
                  fontFamily: "inherit",
                }}
                onFocus={e => e.target.style.borderColor = "rgba(255,255,255,0.5)"}
                onBlur={e => e.target.style.borderColor = wErr ? "#fca5a5" : "rgba(255,255,255,0.25)"}
              />
              <button
                onClick={submitWaitlist}
                style={{ background: C.white, color: "#1e3a8a", border: "none", borderRadius: 10, padding: "14px 28px", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", transition: "all 0.2s", boxShadow: "0 4px 16px rgba(0,0,0,0.15)" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#f0f4ff"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.transform = "none"; }}>
                Quero acesso
              </button>
            </div>
            {wErr && <p style={{ marginTop: 8, fontSize: 13, color: "#fca5a5", textAlign: "left" }}>{wErr}</p>}
            <p style={{ marginTop: 16, fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
              Não partilhamos o seu email com terceiros.
            </p>
          </>
        )}
      </div>
    </section>
  );

  // ── Footer ────────────────────────────────────────────────────────────────────
  const footer = (
    <footer style={{ background: "#080f1d", padding: isMobile ? "40px 24px" : "52px 48px", borderTop: `1px solid ${C.borderLight}` }}>
      <div style={{ maxWidth: 1120, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", gap: 24 }}>
        <div>
          <img src={logoEscuro} alt="VillaGest" style={{ height: 26, width: "auto", marginBottom: 6 }} />
          <p style={{ fontSize: 13, color: C.textLight, marginTop: 6 }}>Gestão de Condomínio</p>
          <p style={{ fontSize: 12, color: "#6b7a8d", marginTop: 4 }}>© {new Date().getFullYear()} VillaGest. Todos os direitos reservados.</p>
        </div>
        <div style={{ display: "flex", gap: 28 }}>
          {[["Funcionalidades","features"],["Como funciona","how"],["Lista de espera","waitlist"]].map(([label, id]) => (
            <button key={id} onClick={() => scrollTo(id)}
              style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, fontSize: 14, fontFamily: "inherit", transition: "color 0.15s", padding: 0 }}
              onMouseEnter={e => e.currentTarget.style.color = C.textMuted}
              onMouseLeave={e => e.currentTarget.style.color = C.textLight}>
              {label}
            </button>
          ))}
          <button onClick={openContact}
            style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, fontSize: 14, fontFamily: "inherit", transition: "color 0.15s", padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = C.textMuted}
            onMouseLeave={e => e.currentTarget.style.color = C.textLight}>
            Contacto
          </button>
          <button onClick={onOpenPrivacy}
            style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, fontSize: 14, fontFamily: "inherit", transition: "color 0.15s", padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.color = C.textMuted}
            onMouseLeave={e => e.currentTarget.style.color = C.textLight}>
            Privacidade
          </button>
        </div>
      </div>
    </footer>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Inter', system-ui, sans-serif", color: C.text, background: C.bg, overflowX: "hidden" }}>
      {navbar}
      <main>
        {hero}
        {features}
        {howItWorks}
        {testimonial}
        {waitlist}
      </main>
      {footer}
      {contactModal}
    </div>
  );
};
